import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AuthContext from '../context/AuthContext';
import ProfileContext from '../context/ProfileContext';
import {
  createMembershipPlan,
  getMembershipPlans,
  updateMembershipPlan,
} from '../api';
import ActiveMembershipsPanel from './ActiveMembershipsPanel';

const EMPTY_FORM = {
  name: '',
  description: '',
  sex: 'both',
  expiration_days: 30,
  price: '',
  sale_percentage: 0,
  included_features: [],
  is_active: true,
};

const durationLabels = {
  30: '1 mois',
  90: '3 mois',
  180: '6 mois',
  365: '1 an',
};

const sexLabels = {
  men: 'Hommes',
  women: 'Femmes',
  both: 'Tous',
};

const centsToEurosInput = (amount) => (
  Number.isFinite(Number(amount)) ? (Number(amount) / 100).toFixed(2) : ''
);

const formatEuros = (amount) => new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
}).format(Number(amount || 0) / 100);

const MembershipPlans = () => {
  const { authTokens } = useContext(AuthContext);
  const { profile } = useContext(ProfileContext);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const featureOptions = useMemo(
    () => Array.from(new Set([
      ...(profile?.features || []),
      'Espace restauration',
      'Parking',
      'Douches',
      'Vestiaires',
      'Sauna',
      'Piscine',
    ])),
    [profile],
  );

  const loadPlans = async () => {
    if (!authTokens?.access) return;
    setLoading(true);
    try {
      setPlans(await getMembershipPlans(authTokens.access));
    } catch (error) {
      setMessage({ severity: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authTokens?.access]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const editPlan = (plan) => {
    setEditingId(plan.id);
    setForm({
      name: plan.name || '',
      description: plan.description || '',
      sex: plan.sex || 'both',
      expiration_days: plan.expiration_days || 30,
      price: centsToEurosInput(plan.price),
      sale_percentage: plan.sale_percentage || 0,
      included_features: plan.included_features || [],
      is_active: plan.is_active,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || Number(form.price) < 0.5) {
      setMessage({ severity: 'error', text: 'Un nom et un prix d’au moins 0,50 € sont obligatoires.' });
      return;
    }

    const priceInCents = Math.round(Number(form.price) * 100);

    const payload = {
      ...form,
      name: form.name.trim(),
      description: form.description.trim(),
      price: priceInCents,
      currency: 'eur',
      expiration_days: Number(form.expiration_days),
      sale_percentage: Number(form.sale_percentage || 0),
      session_count: null,
      sports_field_id: null,
      capacity: null,
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateMembershipPlan(editingId, payload, authTokens.access);
      } else {
        await createMembershipPlan(payload, authTokens.access);
      }
      setMessage({ severity: 'success', text: 'L’abonnement a bien été enregistré.' });
      resetForm();
      await loadPlans();
    } catch (error) {
      setMessage({ severity: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const togglePlan = async (plan) => {
    try {
      await updateMembershipPlan(plan.id, { is_active: !plan.is_active }, authTokens.access);
      setPlans((current) => current.map((item) => (
        item.id === plan.id ? { ...item, is_active: !item.is_active } : item
      )));
    } catch (error) {
      setMessage({ severity: 'error', text: error.message });
    }
  };

  return (
    <Box
      dir="ltr"
      sx={{
        width: '100%',
        maxWidth: 1400,
        minWidth: 0,
        boxSizing: 'border-box',
        overflowX: 'hidden',
        mx: 'auto',
        p: { xs: 2, md: 4 },
      }}
    >
      <Typography variant="h4" fontWeight={800} gutterBottom>
        Abonnements illimités
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Le client paie une seule fois et accède à la salle pendant toute la durée choisie, sans réserver de séance.
      </Typography>

      <Card sx={{ borderRadius: 4, mb: 4 }}>
        <CardContent component="form" onSubmit={submit}>
          <Stack spacing={2.5}>
            <Typography variant="h6" fontWeight={700}>
              {editingId ? 'Modifier l’abonnement' : 'Publier un nouvel abonnement'}
            </Typography>

            <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Nom de l’abonnement"
                  placeholder="Par exemple : Abonnement Gold"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Public</InputLabel>
                  <Select
                    label="Public"
                    value={form.sex}
                    onChange={(event) => setForm({ ...form, sex: event.target.value })}
                  >
                    <MenuItem value="both">Tous</MenuItem>
                    <MenuItem value="men">Hommes</MenuItem>
                    <MenuItem value="women">Femmes</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Durée de validité</InputLabel>
                  <Select
                    label="Durée de validité"
                    value={form.expiration_days}
                    onChange={(event) => setForm({ ...form, expiration_days: event.target.value })}
                  >
                    {Object.entries(durationLabels).map(([days, label]) => (
                      <MenuItem key={days} value={Number(days)}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Prix (€)"
                  value={form.price}
                  onChange={(event) => setForm({ ...form, price: event.target.value })}
                  inputProps={{ min: 0.5, step: 0.01 }}
                  helperText="Le montant sera transmis à Stripe en centimes d’euro."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Réduction (%)"
                  value={form.sale_percentage}
                  onChange={(event) => setForm({ ...form, sale_percentage: event.target.value })}
                  inputProps={{ min: 0, max: 99 }}
                />
              </Grid>
              <Grid item xs={12} md={4} display="flex" alignItems="center">
                <FormControlLabel
                  control={(
                    <Switch
                      checked={form.is_active}
                      onChange={(event) => setForm({ ...form, is_active: event.target.checked })}
                    />
                  )}
                  label="Visible par les clients"
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={featureOptions}
                  value={form.included_features}
                  onChange={(_, value) => setForm({ ...form, included_features: value })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Services inclus"
                      helperText="Sélectionnez un service ou ajoutez-en un, par exemple « Espace restauration »."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Description courte"
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {editingId && <Button onClick={resetForm}>Annuler</Button>}
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={18} /> : <AddRoundedIcon />}
              >
                {editingId ? 'Enregistrer' : 'Publier l’abonnement'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {loading ? (
        <Box textAlign="center"><CircularProgress /></Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              md: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 2,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          {plans.map((plan) => (
              <Card
                key={plan.id}
                sx={{
                  height: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  borderRadius: 4,
                  opacity: plan.is_active ? 1 : 0.65,
                }}
              >
                <CardContent>
                  <Stack spacing={1.5}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" fontWeight={800}>{plan.name}</Typography>
                      <Chip
                        size="small"
                        color={plan.is_active ? 'success' : 'default'}
                        label={plan.is_active ? 'Publié' : 'Inactif'}
                      />
                    </Box>
                    <Typography color="text.secondary">
                      {durationLabels[plan.expiration_days] || `${plan.expiration_days} jours`} · {sexLabels[plan.sex]}
                    </Typography>
                    <Typography variant="h5" fontWeight={900} color="primary">
                      {formatEuros(plan.final_price ?? plan.price)}
                    </Typography>
                    {(plan.included_features || []).slice(0, 5).map((feature) => (
                      <Box key={feature} display="flex" gap={1} alignItems="center">
                        <CheckCircleRoundedIcon color="success" fontSize="small" />
                        <Typography variant="body2">{feature}</Typography>
                      </Box>
                    ))}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" pt={1}>
                      <Button startIcon={<EditRoundedIcon />} onClick={() => editPlan(plan)}>
                        Modifier
                      </Button>
                      <FormControlLabel
                        control={<Switch checked={plan.is_active} onChange={() => togglePlan(plan)} />}
                        label="Actif"
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
          ))}
          {!plans.length && (
            <Alert severity="info" sx={{ gridColumn: '1 / -1' }}>
              Aucun abonnement illimité n’a encore été publié.
            </Alert>
          )}
        </Box>
      )}

      <Box sx={{ mt: 5, width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
        <ActiveMembershipsPanel
          title="Abonnements clients enregistrés"
          showVerifier={false}
        />
      </Box>

      <Snackbar open={Boolean(message)} autoHideDuration={5000} onClose={() => setMessage(null)}>
        <Alert severity={message?.severity || 'info'} onClose={() => setMessage(null)}>
          {message?.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MembershipPlans;
